import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LanguageIcon from '@mui/icons-material/Language';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import XIcon from '@mui/icons-material/X';
import YouTubeIcon from '@mui/icons-material/YouTube';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import SvgIcon from '@mui/material/SvgIcon';
import Typography from '@mui/material/Typography';
import { storyblokEditable } from '@storyblok/react';
import { memo } from 'react';

import { petrol } from '../../../styles/mui/colors';
import useScreenSize from '../../../util/hooks/useScreenSize';
import useStoryblokImage from '../../../util/hooks/useStoryblokImage';
import ProgressiveImage from '../../Image/ProgressiveImage';
import Link from '../../Links/Link/Link';
import { withBaseComponent } from '../hoc/withBaseComponent';

const iconStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  width: '50px',
  height: '50px',
  borderRadius: '8px',
};

const urlIcon = (key) => {
  switch (key) {
    case 'facebook':
      return (
        <Box
          sx={{
            ...iconStyle,
            backgroundColor: '#3b5998',
          }}
        >
          <FacebookIcon />
        </Box>
      );
    case 'instagram':
      return (
        <Box
          sx={{
            ...iconStyle,
            backgroundColor: '#c32aa3',
          }}
        >
          <InstagramIcon />
        </Box>
      );
    case 'linkedin':
      return (
        <Box
          sx={{
            ...iconStyle,
            backgroundColor: '#0077b5',
          }}
        >
          <LinkedInIcon />
        </Box>
      );
    case 'twitter':
      return (
        <Box
          sx={{
            ...iconStyle,
            backgroundColor: '#000',
          }}
        >
          <XIcon />
        </Box>
      );
    case 'website':
      return (
        <Box
          sx={{
            ...iconStyle,
            backgroundColor: petrol.main,
          }}
        >
          <LanguageIcon />
        </Box>
      );
    case 'xing':
      return (
        <Box
          sx={{
            ...iconStyle,
            backgroundColor: '#006567',
          }}
        >
          <SvgIcon>
            <svg stroke="currentColor" viewBox="0 0 384 512" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M162.7 210c-1.8 3.3-25.2 44.4-70.1 123.5-4.9 8.3-10.8 12.5-17.7 12.5H9.8c-7.7 0-12.1-7.5-8.5-14.4l69-121.3c.2 0 .2-.1 0-.3l-43.9-75.6c-4.3-7.8 .3-14.1 8.5-14.1H100c7.3 0 13.3 4.1 18 12.2l44.7 77.5zM382.6 46.1l-144 253v.3L330.2 466c3.9 7.1 .2 14.1-8.5 14.1h-65.2c-7.6 0-13.6-4-18-12.2l-92.4-168.5c3.3-5.8 51.5-90.8 144.8-255.2 4.6-8.1 10.4-12.2 17.5-12.2h65.7c8 0 12.3 6.7 8.5 14.1z" />
            </svg>
          </SvgIcon>
        </Box>
      );
    case 'youtube':
      return (
        <Box
          sx={{
            ...iconStyle,
            backgroundColor: '#ff0000',
          }}
        >
          <YouTubeIcon />
        </Box>
      );
    default:
      return null;
  }
};

/**
 * @param {object} props
 * @param {object} props.blok
 */
const StoryblokPartnerCardComponent = ({ blok }) => {
  const { general } = blok;
  const { name, subTitle, logo, content, urls, reverse } = general;
  const { desktop: desktopReverse, tablet: tabletReverse, mobile: mobileReverse } = reverse;
  const { isMobile, isTablet, isDesktop } = useScreenSize();

  const { src, blurSrc, blurSources, sources } = useStoryblokImage(logo.filename);

  const isReversed =
    isDesktop && desktopReverse
      ? true
      : isTablet && tabletReverse
        ? true
        : isMobile && mobileReverse;
  const direction = isReversed ? 'row-reverse' : 'row';

  return (
    <Grid
      {...storyblokEditable(blok)}
      alignItems="center"
      container
      direction={direction}
      spacing={2}
    >
      <Grid
        size={{
          md: 9,
          sm: 12,
          xs: 12,
        }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: isReversed ? 'flex-end' : 'flex-start',
        }}
      >
        <Typography
          sx={{
            ...(subTitle ? { marginBottom: 0 } : {}),
          }}
          variant="h3"
        >
          {name}
        </Typography>
        {subTitle ? (
          <Typography component="div" sx={{ marginTop: 0 }} variant="subtitle1">
            {subTitle.toUpperCase()}
          </Typography>
        ) : null}
        {content ? (
          <Typography
            component="div"
            sx={{
              ...(isReversed ? { textAlign: 'right' } : {}),
            }}
          >
            {content}
          </Typography>
        ) : null}
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexDirection: 'row',
          }}
        >
          {Object.keys(urls).map((key) => {
            const urlObj = urls[key];
            if (urlObj.url === '') return null;
            return (
              <Link
                key={key}
                sx={{
                  color: '#fff',
                  transition: 'transform 0.3s',
                  '&:hover': {
                    transform: 'scale(1.05)',
                  },
                }}
                target={urlObj.target}
                to={urlObj.url}
              >
                {urlIcon(key)}
              </Link>
            );
          })}
        </Box>
      </Grid>
      <Grid size={{ md: 3, sm: 12, xs: 12 }}>
        <ProgressiveImage
          alt={logo.alt}
          blurSources={blurSources}
          blurSrc={blurSrc}
          imageStyle={{
            objectFit: 'contain',
          }}
          sources={sources}
          src={src}
          title={logo.title}
        />
      </Grid>
    </Grid>
  );
};

StoryblokPartnerCardComponent.displayName = 'StoryblokPartnerCard';
const StoryblokPartnerCard = withBaseComponent(StoryblokPartnerCardComponent);

export default memo(StoryblokPartnerCard);
